import { DOCUMENTS, moodLabel, signedFigure } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, type MonthPage } from '../lib/api.js';
import { useAi } from '../lib/ai.js';
import { DocShell, DocStrip, Figures, Prose } from './Documents.js';
import { dayCell, longDay } from './SharedView.js';

/**
 * The month, for him. Release 5b of 2.0 (docs/PRD-2.0.md §7).
 *
 * One page a month: the account in the app's voice with the threads as its
 * chapters, every day's ending, three or four of his own sentences from the
 * days that ended highest and lowest, and the figures. Twelve of them is the
 * record of a year. The figures are counted on the server from the record;
 * the account is written once the month is over and checked against it.
 */
export function Month(): JSX.Element {
  const { month: param } = useParams<{ month?: string }>();
  const month = param && /^\d{4}-\d{2}$/.test(param) ? param : currentMonth();
  const [page, setPage] = useState<MonthPage | null>(null);
  const [months, setMonths] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { canSummarise } = useAi();

  useEffect(() => {
    setPage(null);
    setError(null);
    void api
      .month(month)
      .then(setPage)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Couldn't load that month."));
  }, [month]);
  useEffect(() => {
    void api.months().then((r) => setMonths(r.months)).catch(() => setMonths([]));
  }, []);

  const prev = months.find((m) => m < month) ?? null;
  const next = months.filter((m) => m > month).sort().at(0) ?? null;

  return (
    <div className="stack-lg stack">
      <Link className="linkish no-print" to="/days">
        ‹ Days
      </Link>
      {error && <div className="banner banner-error">{error}</div>}
      {!page && !error && (
        <div style={{ display: 'grid', placeItems: 'center', minHeight: '40dvh' }}>
          <span className="spinner" />
        </div>
      )}
      {page && <MonthDoc page={page} canSummarise={canSummarise} />}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        {prev ? (
          <Link className="linkish" to={`/month/${prev}`}>
            ‹ {monthName(prev)}
          </Link>
        ) : (
          <span />
        )}
        {next && (
          <Link className="linkish" to={`/month/${next}`}>
            {monthName(next)} ›
          </Link>
        )}
      </div>
    </div>
  );
}

function MonthDoc({ page, canSummarise }: { page: MonthPage; canSummarise: boolean }): JSX.Element {
  const f = page.figures;
  const r = page.report;
  const name = monthName(f.month);
  return (
    <DocShell
      mast={[`${DOCUMENTS.month.name.toLowerCase()} · ${name}`, `${f.days} days · ${f.rated} rated · ${f.entries} ${f.entries === 1 ? 'entry' : 'entries'} · ${f.worksheets} ${f.worksheets === 1 ? 'worksheet' : 'worksheets'}`]}
      foot="Yours only · printable · the same page a year from now will have twelve of these"
    >
      <h2>{name}</h2>

      {r?.status === 'ready' && r.summaryMd ? (
        <Prose text={r.summaryMd} />
      ) : (
        <p className="doc-lead doc-muted">
          {!f.finished
            ? 'The account of this month is written once it is over, from its weekly recaps.'
            : r?.status === 'no_data'
              ? 'Nothing was logged this month, so there is nothing to write from.'
              : r?.status === 'failed'
                ? r.error ?? 'The account could not be written.'
                : !canSummarise
                  ? 'No account for this month: writing one needs the AI setting at “It can write my entries up” or above. The figures and your own words below are from the record.'
                  : 'No account for this month yet: it is written from the month’s weekly recaps, once those exist.'}
        </p>
      )}

      {f.threads.length > 0 && !(r?.status === 'ready') && (
        <p className="doc-muted">
          Running this month: {f.threads.map((t) => `${t.label}${t.kind === 'thread' ? ` (since ${dayCell(t.startedOn)})` : ''}`).join(' · ')}
        </p>
      )}

      <DocStrip days={f.strip} labels />

      {f.words.length > 0 && (
        <>
          <h3>In your words</h3>
          {f.words.map((w) => (
            <div key={w.day} className="doc-answer">
              <span className="doc-when">
                {longDay(w.day)} · ended {moodLabel(w.end).toLowerCase()} {signedFigure(w.end)}
              </span>
              <p className="doc-own">{w.text}</p>
            </div>
          ))}
          <p className="doc-muted">Chosen by the days that ended highest and lowest; quoted exactly, never rewritten.</p>
        </>
      )}

      <h3>The figures</h3>
      <Figures
        items={[
          { value: `${f.rated}/${f.days}`, label: 'days rated' },
          { value: f.floor !== null && f.ceiling !== null ? `${signedFigure(f.floor)} · ${signedFigure(f.ceiling)}` : '—', label: 'floor · ceiling' },
          { value: f.worksheets > 0 ? `${f.worksheetsFinished}/${f.worksheets}` : '—', label: 'worksheets finished' },
          { value: f.doses ? `${f.doses.taken}/${f.doses.scheduled}` : '—', label: 'scheduled doses taken' },
        ]}
      />
      {(f.exerciseDays > 0 || f.helpNow > 0) && (
        <p className="doc-muted">
          {[f.exerciseDays > 0 && `${f.exerciseDays} ${f.exerciseDays === 1 ? 'day' : 'days'} with exercise marked`, f.helpNow > 0 && `${f.helpNow} Help now ${f.helpNow === 1 ? 'moment' : 'moments'}`]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
    </DocShell>
  );
}

export function monthName(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

